/**
 * Which fields a command writes, finer than which document.
 *
 * `recipeScopeOf` (in `scope.ts`) answers "which recipe", and that is enough to
 * build a recipe's timeline. It is not enough for undo in a shared recipe
 * (PLAN-COLLAB D-CO10). Today a remote delta clears the whole undo stack, which
 * is right for one person on two devices and unusable for two people at once:
 * every hop your collaborator nudged would wipe your history. So the store asks
 * this question of each remote command instead, keeps every stacked inverse
 * whose fields it did not touch, and drops the ones it did.
 *
 * A {@link FieldScope} is a document, then a field or list of it, then a row of
 * that list. `updateHop` on row `a` and `updateHop` on row `b` stay apart;
 * `removeHop` and `reorderHops` name the whole list, so they meet every row.
 *
 * A `switch` with no `default`, so the compiler checks it is total, for the same
 * reason `scope.ts` has one: adding a command forces a decision about what it
 * writes. When a command writes more than one document, or the answer is not
 * obvious, it says `'*'`. Too broad costs someone an undo entry; too narrow
 * keeps an inverse that would write over the other person's edit.
 *
 * Every recipe command also stamps `updatedAt`. That is bookkeeping rather than
 * a field anyone edits, and naming it would make every recipe command overlap
 * every other, so no scope mentions it.
 */

import type { Command } from './types';

/** One place a command writes. */
export interface FieldScope {
  /** `recipes:<id>`, `batches:<id>`, `settings`, … or `'*'` for "could be anything". */
  readonly doc: string;
  /** A field or list of that document (`name`, `hops`, `water`, …), or `'*'` for all of it. */
  readonly field: string;
  /** The row of that list, or `null` for the whole field. */
  readonly row: string | null;
}

/** Could be anything: the answer for every command that writes more than one document. */
export const ANYWHERE: readonly FieldScope[] = [{ doc: '*', field: '*', row: null }];

/** A whole document of `collection`. */
function wholeDoc(collection: string, id: string): readonly FieldScope[] {
  return [{ doc: `${collection}:${id}`, field: '*', row: null }];
}

/** Several whole fields of one recipe, for a setter that writes more than one. */
function recipeFields(recipeId: string, ...fields: string[]): readonly FieldScope[] {
  return fields.map((field) => ({ doc: `recipes:${recipeId}`, field, row: null }));
}

/** One field of one recipe, or one row of one of its lists. */
function recipeField(
  recipeId: string,
  field: string,
  row: string | null = null,
): readonly FieldScope[] {
  return [{ doc: `recipes:${recipeId}`, field, row }];
}

/**
 * The places `command` writes. Never empty.
 *
 * Scalar setters name the document field they set. Where one setter can write
 * a second field too (`setBatchSize` may move `batchVolumeBasis`, `setPackaging`
 * may set `primingSugar` beside `packagingMethod`), it names both, whether or
 * not this payload carries the second. Naming only the first was enough while no
 * other command wrote the second on its own, but that holds only until someone
 * adds a `setPrimingSugar`, and then the narrow answer would silently keep an
 * inverse that writes over a collaborator's edit. Naming both costs nothing
 * today, since nothing else writes either field.
 */
export function fieldScopeOf(command: Command): readonly FieldScope[] {
  switch (command.type) {
    // The whole recipe: lifecycle, and the two commands that rewrite every field.
    case 'recipe.restore':
      return recipeField(command.payload.recipe.id, '*');
    case 'recipe.create':
    case 'recipe.delete':
    case 'recipe.scale':
    case 'recipe.revertTo':
      return recipeField(command.payload.recipeId, '*');

    // Scalar setters, by the document field each one sets.
    case 'recipe.rename':
      return recipeField(command.payload.recipeId, 'name');
    case 'recipe.setStyle':
      return recipeField(command.payload.recipeId, 'styleId');
    case 'recipe.setTargetOg':
      return recipeField(command.payload.recipeId, 'targetOg');
    case 'recipe.setTargetSrm':
      return recipeField(command.payload.recipeId, 'targetSrm');
    case 'recipe.setArchived':
      return recipeField(command.payload.recipeId, 'archivedAt');
    case 'recipe.setBatchSize':
      return recipeFields(command.payload.recipeId, 'batchVolumeL', 'batchVolumeBasis');
    case 'recipe.setBoilTime':
      return recipeField(command.payload.recipeId, 'boilTimeMinutes');
    case 'recipe.setEquipment':
      return recipeField(command.payload.recipeId, 'equipmentProfileId');
    case 'recipe.setEfficiency':
      return recipeField(command.payload.recipeId, 'mashEfficiencyPercentOverride');
    case 'recipe.setCarbonation':
      return recipeField(command.payload.recipeId, 'carbonationVolumes');
    case 'recipe.setPackaging':
      return recipeFields(command.payload.recipeId, 'packagingMethod', 'primingSugar');
    case 'recipe.setNotes':
      return recipeField(command.payload.recipeId, 'notes');
    case 'recipe.setTags':
      return recipeField(command.payload.recipeId, 'tags');

    // One row of a list, by the row id the payload carries.
    case 'recipe.updateFermentable':
      return recipeField(command.payload.recipeId, 'fermentables', command.payload.itemId);
    case 'recipe.updateHop':
      return recipeField(command.payload.recipeId, 'hops', command.payload.itemId);
    case 'recipe.updateYeast':
      return recipeField(command.payload.recipeId, 'yeasts', command.payload.itemId);
    case 'recipe.updateMisc':
      return recipeField(command.payload.recipeId, 'miscs', command.payload.itemId);
    case 'recipe.updateMashStep':
      return recipeField(command.payload.recipeId, 'mashSteps', command.payload.stepId);
    case 'recipe.updateFermentationStep':
      return recipeField(command.payload.recipeId, 'fermentationSteps', command.payload.stepId);

    // Adding, removing and reordering change the list's shape, so they name all
    // of it: an undo that re-inserts at an index is only right if nothing else
    // moved the rows around it.
    case 'recipe.addFermentable':
    case 'recipe.removeFermentable':
    case 'recipe.reorderFermentables':
      return recipeField(command.payload.recipeId, 'fermentables');
    case 'recipe.addHop':
    case 'recipe.removeHop':
    case 'recipe.reorderHops':
      return recipeField(command.payload.recipeId, 'hops');
    case 'recipe.addYeast':
    case 'recipe.removeYeast':
    case 'recipe.reorderYeasts':
      return recipeField(command.payload.recipeId, 'yeasts');
    case 'recipe.addMisc':
    case 'recipe.removeMisc':
    case 'recipe.reorderMiscs':
      return recipeField(command.payload.recipeId, 'miscs');
    case 'recipe.addMashStep':
    case 'recipe.removeMashStep':
    case 'recipe.reorderMashSteps':
      return recipeField(command.payload.recipeId, 'mashSteps');
    case 'recipe.addFermentationStep':
    case 'recipe.removeFermentationStep':
    case 'recipe.reorderFermentationSteps':
      return recipeField(command.payload.recipeId, 'fermentationSteps');

    // The water section is one nested value on the recipe, and `water.clear`
    // nulls all of it, so every water command names the whole field.
    case 'water.setSourceProfile':
    case 'water.setDilution':
    case 'water.setSaltAdditions':
    case 'water.setAcidAdditions':
    case 'water.setTargetProfile':
    case 'water.setTargetRule':
    case 'water.setTargetPh':
    case 'water.setKettleTargetPh':
    case 'water.clear':
      return recipeField(command.payload.recipeId, 'water');

    // Documents of their own, each written whole.
    case 'recipeSnapshot.restore':
      return wholeDoc('recipeSnapshots', command.payload.snapshot.id);
    case 'recipeSnapshot.create':
    case 'recipeSnapshot.delete':
    case 'recipeSnapshot.rename':
      return wholeDoc('recipeSnapshots', command.payload.snapshotId);

    case 'equipment.restore':
      return wholeDoc('equipmentProfiles', command.payload.profile.id);
    case 'equipment.create':
    case 'equipment.update':
    case 'equipment.delete':
      return wholeDoc('equipmentProfiles', command.payload.profileId);

    case 'mashProfile.restore':
      return wholeDoc('mashProfiles', command.payload.profile.id);
    case 'mashProfile.create':
    case 'mashProfile.update':
    case 'mashProfile.delete':
      return wholeDoc('mashProfiles', command.payload.profileId);

    case 'fermentationProfile.restore':
      return wholeDoc('fermentationProfiles', command.payload.profile.id);
    case 'fermentationProfile.create':
    case 'fermentationProfile.update':
    case 'fermentationProfile.delete':
      return wholeDoc('fermentationProfiles', command.payload.profileId);

    case 'miscProfile.restore':
      return wholeDoc('miscProfiles', command.payload.profile.id);
    case 'miscProfile.create':
    case 'miscProfile.update':
    case 'miscProfile.delete':
      return wholeDoc('miscProfiles', command.payload.profileId);

    case 'waterProfile.restore':
      return wholeDoc('waterProfiles', command.payload.profile.id);
    case 'waterProfile.create':
    case 'waterProfile.update':
    case 'waterProfile.delete':
      return wholeDoc('waterProfiles', command.payload.profileId);

    // `batch.setInventoryDeducted` and `batch.setPitchedYeast` ride beside the
    // `inventory.adjustQuantity` commands that move stock, but each of them
    // writes only the batch.
    case 'batch.restore':
      return wholeDoc('batches', command.payload.batch.id);
    case 'batch.create':
    case 'batch.resnapshot':
    case 'batch.delete':
    case 'batch.rename':
    case 'batch.setStatus':
    case 'batch.setBrewedAt':
    case 'batch.setPackagedOn':
    case 'batch.updateMeasurements':
    case 'batch.addFermentationReading':
    case 'batch.removeFermentationReading':
    case 'batch.setStageNotes':
    case 'batch.toggleStep':
    case 'batch.setStepTimer':
    case 'batch.setInventoryDeducted':
    case 'batch.setPitchedYeast':
    case 'batch.setPackaging':
    case 'batch.setYeastStarter':
    case 'batch.setYeastPitchedAt':
    case 'batch.addCellarTask':
    case 'batch.updateCellarTask':
    case 'batch.removeCellarTask':
    case 'batch.addTasting':
    case 'batch.updateTasting':
    case 'batch.removeTasting':
    case 'batch.addPhoto':
    case 'batch.setPhotoCaption':
    case 'batch.removePhoto':
      return wholeDoc('batches', command.payload.batchId);

    case 'blend.restore':
      return wholeDoc('blends', command.payload.blend.id);
    case 'blend.create':
    case 'blend.update':
    case 'blend.setStage':
    case 'blend.delete':
    case 'blend.addCellarTask':
    case 'blend.updateCellarTask':
    case 'blend.removeCellarTask':
      return wholeDoc('blends', command.payload.blendId);

    case 'journal.restore':
      return wholeDoc('journals', command.payload.journal.id);
    case 'journal.create':
    case 'journal.update':
    case 'journal.delete':
    case 'journal.addBlock':
    case 'journal.setBlock':
    case 'journal.moveBlock':
    case 'journal.removeBlock':
      return wholeDoc('journals', command.payload.journalId);

    // `inventory.add` reads the custom-ingredient library for a linked lot's
    // label, but writes only the lot.
    case 'inventory.restore':
      return wholeDoc('inventory', command.payload.item.id);
    case 'inventory.add':
    case 'inventory.update':
    case 'inventory.remove':
    case 'inventory.adjustQuantity':
      return wholeDoc('inventory', command.payload.itemId);

    // Adding an entry writes only the entry. The other three reach into the
    // inventory as well: an update relabels the entry's linked lots, a removal
    // can take its empty lots with it, and a restore puts them back.
    case 'customIngredient.add':
      return wholeDoc('customIngredients', command.payload.ingredientId);
    case 'customIngredient.update':
    case 'customIngredient.remove':
    case 'customIngredient.restore':
      return ANYWHERE;

    case 'settings.update':
      return [{ doc: 'settings', field: '*', row: null }];

    // Sharing reaches across collections: `recipe.share` takes a recipe and its
    // snapshots, `sharedRecipe.leave` adds one and repoints batches. The override
    // writes only the reference, but it moves every number on a shared recipe,
    // so it is not narrowed either.
    case 'recipe.share':
    case 'sharedRecipe.join':
    case 'sharedRecipe.leave':
    case 'sharedRecipe.setEquipmentOverride':
      return ANYWHERE;
  }
}

/**
 * Whether two scopes could name the same value.
 *
 * `'*'` meets everything at its level, and a `null` row is the whole list, so it
 * meets every row of it.
 */
export function scopesOverlap(a: FieldScope, b: FieldScope): boolean {
  if (a.doc === '*' || b.doc === '*') return true;
  if (a.doc !== b.doc) return false;
  if (a.field === '*' || b.field === '*') return true;
  if (a.field !== b.field) return false;
  return a.row === null || b.row === null || a.row === b.row;
}

/**
 * Whether any command in `a` writes somewhere any command in `b` writes.
 *
 * An undo entry is often several commands (one press of *Scale*, a brew-day
 * deduction), and a remote delta can be several too; they overlap if any pair
 * does.
 */
export function commandsOverlap(a: readonly Command[], b: readonly Command[]): boolean {
  const bScopes = b.flatMap(fieldScopeOf);
  return a.some((command) =>
    fieldScopeOf(command).some((scope) => bScopes.some((other) => scopesOverlap(scope, other))),
  );
}
